import React, { useState } from 'react';
import { FiZap, FiTrendingUp, FiDollarSign, FiTarget, FiCheckCircle, FiAlertTriangle } from 'react-icons/fi';

const TurpeOptimizationCard = ({ userProfile }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const optimizationData = userProfile.turpe_optimization || {
    current_annual_cost: 0,
    optimized_annual_cost: 0,
    potential_savings: 0,
    optimization_actions: []
  };

  const savingsPercentage = optimizationData.current_annual_cost > 0 
    ? ((optimizationData.potential_savings / optimizationData.current_annual_cost) * 100).toFixed(1)
    : 0;

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold">Optimisation TURPE</h3>
            <p className="text-blue-100">Analyse et recommandations personnalisées</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold">{savingsPercentage}%</div>
            <div className="text-sm text-blue-100">d'économies</div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Résumé des économies */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="text-center p-4 bg-red-50 rounded-lg">
            <FiDollarSign className="w-8 h-8 text-red-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-red-600">
              {optimizationData.current_annual_cost.toLocaleString('fr-FR')}€
            </div>
            <div className="text-sm text-gray-600">Coût actuel</div>
          </div>
          
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <FiTarget className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-green-600">
              {optimizationData.optimized_annual_cost.toLocaleString('fr-FR')}€
            </div>
            <div className="text-sm text-gray-600">Coût optimisé</div>
          </div>
          
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <FiTrendingUp className="w-8 h-8 text-blue-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-blue-600">
              {optimizationData.potential_savings.toLocaleString('fr-FR')}€
            </div>
            <div className="text-sm text-gray-600">Économies annuelles</div>
          </div>
        </div>

        {/* Actions d'optimisation */}
        {optimizationData.optimization_actions && optimizationData.optimization_actions.length > 0 && (
          <div className="mb-6">
            <h4 className="font-semibold text-gray-900 mb-3">Actions recommandées</h4>
            <div className="space-y-3">
              {optimizationData.optimization_actions.slice(0, isExpanded ? undefined : 3).map((action, index) => (
                <div key={index} className="flex items-start p-3 bg-gray-50 rounded-lg">
                  <div className="flex-shrink-0 mr-3">
                    {action.type === 'power_reduction' && <FiTrendingUp className="w-5 h-5 text-green-600" />}
                    {action.type === 'contract_optimization' && <FiTarget className="w-5 h-5 text-blue-600" />}
                    {action.type === 'monitoring' && <FiZap className="w-5 h-5 text-purple-600" />}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{action.title}</p>
                    <p className="text-sm text-gray-600 mt-1">{action.description}</p>
                    <div className="flex items-center mt-2">
                      <span className="text-sm text-green-600 font-medium">
                        Économie: {action.savings}€/an
                      </span>
                      {action.status === 'completed' && (
                        <FiCheckCircle className="w-4 h-4 text-green-500 ml-2" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {optimizationData.optimization_actions.length > 3 && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="mt-3 text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                {isExpanded ? 'Voir moins' : `Voir ${optimizationData.optimization_actions.length - 3} actions supplémentaires`}
              </button>
            )}
          </div>
        )}

        {/* Prochaines étapes */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-blue-900 mb-2">Prochaines étapes</h4>
          <div className="space-y-2 text-sm text-blue-800">
            <div className="flex items-center">
              <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
              <span>Analyser vos données de consommation des 12 derniers mois</span>
            </div>
            <div className="flex items-center">
              <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
              <span>Identifier les opportunités d'optimisation</span>
            </div>
            <div className="flex items-center">
              <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
              <span>Mettre en place les actions recommandées</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TurpeOptimizationCard;